import { CurrencyUtils } from '@Core/Currencies/CurrencyUtils';
import { GameScene, IOdometerCheckpoint } from '@Core/index';
import { ELayeringOrder } from '@GFTypes/LayeringOrder';
import EventsCenter from '@Managers/Events/EventsCenter';
import gsap from 'gsap';
import { BitmapText, Container } from 'pixi.js';

export interface IOdometerConfig {
    fontKey: string;
    fontSize: number;
    hideAfterSpinDelay: number;
    holdDuration: number;
    maxWidth?: number;
}

export default class Odometer {
    private readonly scene: GameScene;
    private readonly config: IOdometerConfig;

    private container: Container;
    private text: BitmapText;

    private value = 0;
    public tween: gsap.core.Tween | null = null;

    private isRunning = false;
    private skipRequested = false;
    private isInsideCheckpoint = false;
    private runCompletion: Promise<void> = Promise.resolve();
    private resolveRunCompletion: (() => void) | null = null;

    constructor(scene: GameScene, config: IOdometerConfig) {
        this.scene = scene;
        this.config = config;
        this.create();
    }

    public getContainer(): Container {
        return this.container;
    }

    public async start(params: {
        targetValue: number;
        duration: number;
        checkpoints?: IOdometerCheckpoint[];
        onUpdate?: (tween: gsap.core.Tween, text: BitmapText) => void;
        onComplete?: () => void;
    }): Promise<void> {
        this.stop();
        this.show();
        this.isRunning = true;

        this.skipRequested = false;

        this.runCompletion = new Promise<void>((resolve) => {
            this.resolveRunCompletion = resolve;
        });

        const checkpoints = params.checkpoints ?? [];

        const sortedCheckpoints = checkpoints
            .filter((checkpoint) => checkpoint.value >= this.value && checkpoint.value <= params.targetValue)
            .sort((a, b) => a.value - b.value);

        const values = [
            ...sortedCheckpoints.map((checkpoint) => ({
                value: checkpoint.value,
                checkpoint
            })),
            {
                value: params.targetValue,
                checkpoint: undefined
            }
        ];

        const startValue = this.value;
        const totalDistance = params.targetValue - startValue;

        for (const point of values) {
            if (this.skipRequested) break;

            const distance = point.value - this.value;
            const duration = totalDistance === 0 ? 0 : params.duration * (distance / totalDistance);

            await this.tweenTo({
                targetValue: point.value,
                duration,
                onUpdate: params.onUpdate
            });

            if (point.checkpoint && !this.skipRequested) {
                this.isInsideCheckpoint = true;

                EventsCenter.emit(point.checkpoint.eventToEmit);

                await EventsCenter.waitForEvent(point.checkpoint.waitForEvent);

                this.isInsideCheckpoint = false;
            }
        }

        this.updateValue(params.targetValue);

        this.isRunning = false;
        this.tween = null;

        this.resolveRunCompletion?.();
        this.resolveRunCompletion = null;

        params.onComplete?.();
    }

    public hide(): void {
        this.stop();
        this.value = 0;
        this.text.visible = false;
    }

    public skipToEnd(): void {
        this.skipRequested = true;
        this.tween?.progress(1);
    }

    public waitForTweenComplete(): Promise<void> {
        if (!this.isRunning) return Promise.resolve();
        return this.runCompletion;
    }

    public setFont(fontKey: string, fontSize?: number): void {
        this.text.style.fontFamily = fontKey;

        if (fontSize) {
            this.text.style.fontSize = fontSize;
        }

        this.renderText(this.text.text);
    }

    public getIsInsideCheckpoint(): boolean {
        return this.isInsideCheckpoint;
    }

    private tweenTo(params: { targetValue: number; duration: number; onUpdate?: (tween: gsap.core.Tween, text: BitmapText) => void }): Promise<void> {
        return new Promise((resolve) => {
            this.tween = gsap.to(this, {
                value: params.targetValue,
                duration: params.duration / 1000,
                onUpdate: () => {
                    this.updateValue(this.value);
                    params.onUpdate?.(this.tween!, this.text);
                },
                onComplete: () => {
                    this.updateValue(params.targetValue);
                    resolve();
                }
            });
        });
    }

    private create(): void {
        this.container = new Container();

        this.text = new BitmapText({
            text: '',
            style: {
                fontFamily: this.config.fontKey,
                fontSize: this.config.fontSize
            }
        });

        this.text.anchor.set(0.5, 1);
        this.text.visible = false;

        this.container.addChild(this.text);
        this.container.zIndex = ELayeringOrder.ODOMETER;
        this.scene.stage.addChild(this.container);
    }

    private updateValue(value: number): void {
        this.value = value;
        this.renderText(this.formatValue(value));
    }

    private renderText(text: string): void {
        this.text.text = text;
        this.text.scale.set(this.calculateScale(text));
    }

    private formatValue(value: number): string {
        return CurrencyUtils.formatRawCurrencyValue(value, {
            withSymbol: false,
            decimalSeparator: '.'
        });
    }

    private calculateScale(text: string): number {
        if (!this.config.maxWidth) return 1;

        const temp = new BitmapText({
            text,
            style: {
                fontFamily: this.config.fontKey,
                fontSize: this.config.fontSize
            }
        });

        const scale = Math.min(1, this.config.maxWidth / temp.width);

        temp.destroy();

        return scale;
    }

    private show(): void {
        this.text.visible = true;
    }

    private stop(): void {
        this.tween?.kill();
        this.tween = null;
        this.isRunning = false;
        this.isInsideCheckpoint = false;
        this.resolveRunCompletion?.();
        this.resolveRunCompletion = null;
    }

    public getValue(): number {
        return this.value;
    }
}
